// File: /src/components/Administration/Planner/PlannerEventModal.js
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, DatePicker, message, Row, Col } from "antd";
import dayjs from "dayjs";
import { fetchAvailablePlannerDevicesAdmin } from "./plannerAdminService";
import { AppleOutlined, WindowsOutlined, AndroidOutlined } from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

const PlannerEventModal = ({
                               open,
                               onCancel,
                               onSubmit,
                               plannerEvent,
                               clients,
                               events,
                               submitting
                           }) => {
    const [form] = Form.useForm();
    const { darkTheme } = useTheme();
    const [devicesLoading, setDevicesLoading] = useState(false);
    const [availableDevices, setAvailableDevices] = useState([]);

    const selectedDates = Form.useWatch("dates", form);
    const selectedType = Form.useWatch("deviceType", form);
    const selectedEventId = Form.useWatch("eventID", form);

    const windowsStyle = {
        fontSize: 16,
        marginRight: 8,
        color: darkTheme ? '#60A5FA' : '#1677FF'
    };

    const platformIcons = {
        iOS: <AppleOutlined style={{ fontSize: 16, marginRight: 8 }} />,
        MacCatalyst: <AppleOutlined style={{ fontSize: 16, marginRight: 8 }} />,
        Android: (
            <AndroidOutlined
                style={{
                    fontSize: 16,
                    marginRight: 8,
                    color: darkTheme ? '#A4C639' : '#3DDC84'
                }}
            />
        ),
        Windows: <WindowsOutlined style={windowsStyle} />,
        WinUI: <WindowsOutlined style={windowsStyle} />
    };

    // Initialize form values
    useEffect(() => {
        if (!open) return;

        if (plannerEvent) {
            form.setFieldsValue({
                title: plannerEvent.title || "",
                clientID: plannerEvent.clientID?._id || plannerEvent.clientID || undefined,
                eventID: plannerEvent.eventID?._id || plannerEvent.eventID || undefined,
                dates: plannerEvent.startDate && plannerEvent.endDate
                    ? [dayjs(plannerEvent.startDate), dayjs(plannerEvent.endDate)]
                    : undefined,
                status: plannerEvent.status || "draft",
                notes: plannerEvent.notes || "",
                deviceType: undefined,
                plannedDevices: Array.isArray(plannerEvent.plannedDevices)
                    ? plannerEvent.plannedDevices.map(device => device?._id || device).filter(Boolean)
                    : []
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                status: "draft",
                plannedDevices: []
            });
        }
    }, [open, plannerEvent, form]);

    // Auto-fill title/client from selected event when creating
    useEffect(() => {
        if (!open || plannerEvent) return;
        if (!selectedEventId) return;

        const selectedEvent = events.find(ev => ev._id === selectedEventId);
        if (!selectedEvent) return;

        form.setFieldsValue({
            title: selectedEvent.name || selectedEvent.mnemonic || "",
            clientID: selectedEvent.clientID?._id || selectedEvent.clientID || form.getFieldValue("clientID")
        });
    }, [selectedEventId, events, plannerEvent, open, form]);

    // Load available devices when dates or type change
    useEffect(() => {
        const loadAvailableDevices = async () => {
            if (!open) return;
            if (!selectedDates || selectedDates.length !== 2) {
                setAvailableDevices([]);
                return;
            }

            try {
                setDevicesLoading(true);

                const [startDate, endDate] = selectedDates;

                const devices = await fetchAvailablePlannerDevicesAdmin({
                    startDate: dayjs(startDate).toISOString(),
                    endDate: dayjs(endDate).toISOString(),
                    type: selectedType || undefined
                });

                setAvailableDevices(Array.isArray(devices) ? devices : []);
            } catch (error) {
                console.error("Error fetching available planner devices:", error);
                message.error("Error loading available devices");
                setAvailableDevices([]);
            } finally {
                setDevicesLoading(false);
            }
        };

        loadAvailableDevices();
    }, [open, selectedDates, selectedType]);

    useEffect(() => {
        if (!open) return;

        if (plannerEvent) {
            const existingDevices = Array.isArray(plannerEvent.plannedDevices)
                ? plannerEvent.plannedDevices
                : [];

            setAvailableDevices(prev => {
                const map = new Map();

                [...existingDevices, ...prev].forEach(d => {
                    if (!d?._id) return;
                    map.set(d._id, d);
                });

                return Array.from(map.values());
            });
        }
    }, [open, plannerEvent]);

    const deviceTypeOptions = useMemo(() => {
        const types = Array.from(
            new Set(
                availableDevices
                    .map(device => device?.Type)
                    .filter(Boolean)
            )
        );

        return types.sort((a, b) => a.localeCompare(b));
    }, [availableDevices]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            const payload = {
                title: values.title,
                clientID: values.clientID,
                eventID: values.eventID || null,
                startDate: values.dates?.[0]?.toISOString(),
                endDate: values.dates?.[1]?.toISOString(),
                plannedDevices: values.plannedDevices || [],
                status: values.status,
                notes: values.notes || ""
            };

            onSubmit(payload);
        } catch (error) {
            // Validation handled by Form
        }
    };

    return (
        <Modal
            open={open}
            title={plannerEvent ? "Edit Planner Event" : "Create Planner Event"}
            onCancel={onCancel}
            onOk={handleOk}
            confirmLoading={submitting}
            width={900}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="title"
                            label="Title"
                            rules={[{ required: true, message: "Title is required" }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="clientID"
                            label="Client"
                            rules={[{ required: true, message: "Client is required" }]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="children"
                                placeholder="Select client"
                            >
                                {clients.map(client => (
                                    <Option key={client._id} value={client._id}>
                                        {client.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="eventID"
                            label="Linked Event"
                        >
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="children"
                                placeholder="Optional event"
                            >
                                {events.map(event => (
                                    <Option key={event._id} value={event._id}>
                                        {event.mnemonic ? `${event.mnemonic} - ${event.name}` : event.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="status"
                            label="Status"
                            rules={[{ required: true, message: "Status is required" }]}
                        >
                            <Select>
                                <Option value="draft">Draft</Option>
                                <Option value="confirmed">Confirmed</Option>
                                <Option value="completed">Completed</Option>
                                <Option value="cancelled">Cancelled</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="dates"
                    label="Date Range"
                    rules={[{ required: true, message: "Date range is required" }]}
                >
                    <RangePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                </Form.Item>

                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="deviceType"
                            label="Device Type"
                        >
                            <Select allowClear placeholder="All devices">
                                <Option value="Mobile">Mobile</Option>
                                <Option value="Desktop">Desktop</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={16}>
                        <Form.Item
                            name="plannedDevices"
                            label="Planned Devices"
                        >
                            <Select
                                mode="multiple"
                                loading={devicesLoading}
                                showSearch
                                optionFilterProp="children"
                                placeholder="Select available devices"
                                onDropdownVisibleChange={(open) => {
                                    if (open && (!selectedDates || selectedDates.length !== 2)) {
                                        message.warning("Select a date range first");
                                    }
                                }}
                                disabled={!selectedDates || selectedDates.length !== 2}
                            >
                                {availableDevices.map(device => (
                                    <Option key={device._id} value={device._id}>
                                        <span style={{ display: "flex", alignItems: "center" }}>
                                            {platformIcons[device.Platform]}
                                            {device.Name || "-"}
                                        </span>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="notes"
                    label="Notes"
                >
                    <TextArea rows={4} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PlannerEventModal;